import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Trophy, Upload, User, Share2, Copy, Check, Download, Image, Sword, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import Avatar from "./Avatar";
import { useLanguage } from "@/contexts/LanguageContext";
import ResultsTable from "./ResultsTable";
import PlaceMedal from './PlaceMedal';
import SiteFooter from './SiteFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserResults = () => {
  const { gameId, userId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const { t, language } = useLanguage();
  const getTranslatedText = (translations, fallback) => {
    if (!translations) return fallback;
    if (translations[language]) return translations[language];
    if (translations['ru']) return translations['ru'];
    return fallback;
  };
  const [game, setGame] = useState(null);
  const [panoramas, setPanoramas] = useState([]);
  const [userResults, setUserResults] = useState(null);
  const [allResults, setAllResults] = useState([]);
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [copied, setCopied] = useState(false);
  const [generatingCard, setGeneratingCard] = useState(false);
  const [creatingChallenge, setCreatingChallenge] = useState(false);
  const [hotspotsStats, setHotspotsStats] = useState(null);

  useEffect(() => {
    fetchData();
  }, [gameId, userId]);


  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch game info (no auth required for public viewing)
      const gameResponse = await axios.get(`${API}/games/${gameId}`);
      setGame(gameResponse.data);
      
      // Fetch panoramas
      const panoramasResponse = await axios.get(`${API}/games/${gameId}/panoramas`);
      setPanoramas(panoramasResponse.data);
      
      // Fetch winners
      const winnersResponse = await axios.get(`${API}/games/${gameId}/winners`);
      setWinners(winnersResponse.data);
      
      // Fetch user results
      const resultsResponse = await axios.get(`${API}/games/${gameId}/results`);
      const results = resultsResponse.data;
      const targetUserResult = results.find(r => r.user_id === userId);
      
      if (!targetUserResult) {
        toast.error(t('results.userNotFound'));
        navigate(`/${gameId}`);
        return;
      }
      
      setUserResults(targetUserResult);
      setAllResults(results);
      
      // Fetch hotspot statistics for this user
      try {
        const hotspotsResponse = await axios.get(`${API}/games/${gameId}/users/${userId}/hotspots-stats`);
        setHotspotsStats(hotspotsResponse.data);
      } catch (e) {
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(t('results.loadError'));
      navigate(`/${gameId}`);
    } finally {
      setLoading(false);
    }
  };

  const getPanoramaResult = (panoramaId) => {
    if (!userResults || !userResults.answers) return null;
    return userResults.answers.find(a => a.panorama_id === panoramaId);
  };

  // Share functions
  const getShareUrl = () => {
    return `https://countryscanner.ru/game/${gameId}/user-results/${userId}`;
  };

  const getShareText = () => {
    if (!userResults || !game) return '';
    
    // Find user's actual place from winners API
    const userWinner = winners.find(w => w.user_id === userId);
    const maxPossibleScore = userResults.total_panoramas * 5000;
    const accuracyPercent = maxPossibleScore > 0 
      ? ((userResults.total_score / maxPossibleScore) * 100).toFixed(1)
      : 0;
    
    let positionText = '';
    if (userWinner) {
      if (userWinner.place === 1) positionText = '1-е место 🥇';
      else if (userWinner.place === 2) positionText = '2-е место 🥈';
      else if (userWinner.place === 3) positionText = '3-е место 🥉';
    } else {
      // Not a winner, calculate position by score
      const sortedResults = [...allResults].sort((a, b) => b.total_score - a.total_score);
      const userPosition = sortedResults.findIndex(r => r.user_id === userId) + 1;
      positionText = `${userPosition}-е место`;
    }
    
    return `Я занял ${positionText} в игре "${getTranslatedText(game.name_translations, game.name)}"! Набрал ${userResults.total_score.toLocaleString('ru-RU')} очков с точностью ${accuracyPercent}%`;
  };

  const handleCopyLink = () => {
    const url = getShareUrl();
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast.success(t('userResults.linkBeenCopied'));
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      toast.error(t('userResults.copyError'));
    });
  };

  const handleShareVK = () => {
    const url = getShareUrl();
    const text = getShareText();
    window.open(`https://vk.com/share.php?url=${encodeURIComponent(url)}&title=${encodeURIComponent(text)}`, '_blank');
  };

  const handleShareTelegram = () => {
    const url = getShareUrl();
    const text = getShareText();
    window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleShareWhatsApp = () => {
    const url = getShareUrl();
    const text = getShareText();
    window.open(`https://wa.me/?text=${encodeURIComponent(text + '\n' + url)}`, '_blank');
  };

  const handleGenerateCard = async () => {
    setGeneratingCard(true);
    try {
      const response = await fetch(`${API}/games/${gameId}/user-results/${userId}/share-card`);
      if (!response.ok) {
        throw new Error('Failed to generate card');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `panoguesser-result-${gameId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('userResults.cardLoaded'));
    } catch (error) {
      console.error('Error generating card:', error);
      toast.error(t('userResults.cardGenerationError'));
    } finally {
      setGeneratingCard(false);
    }
  };

  const handleCreateChallenge = async () => {
    if (!user) {
      toast.error(t('userResults.logCreateChallenge'));
      navigate('/login');
      return;
    }

    setCreatingChallenge(true);
    try {
      const response = await axios.post(
        `${API}/challenges`,
        { game_id: gameId },
        { headers: getAuthHeader() }
      );
      
      toast.success(t('userResults.challengeBeenCreated'));
      
      // Copy challenge URL to clipboard
      const challengeUrl = response.data.challenge_url;
      try {
        await navigator.clipboard.writeText(challengeUrl);
        toast.info(t('userResults.linkBeenCopiedClipboard'));
      } catch (e) {
        // Clipboard might not be available
      }
      
      // Navigate to challenge page
      navigate(`/challenge/${response.data.challenge_id}`);
    } catch (error) {
      console.error('Error creating challenge:', error);
      toast.error(error.response?.data?.detail || 'Ошибка создания челленджа');
    } finally {
      setCreatingChallenge(false);
    }
  };

  if (loading) {
    return (
      <div className="gallery-container">
        <div className="loading-state">{t('userResults.loading')}</div>
      </div>
    );
  }

  if (!game || !userResults) {
    return null;
  }

  // Calculate accuracy
  const maxPossibleScore = userResults.total_panoramas * 5000;
  const accuracyPercent = maxPossibleScore > 0 
    ? ((userResults.total_score / maxPossibleScore) * 100).toFixed(1)
    : 0;

  return (
    <>
    <div className="gallery-container" style={{ 
      maxWidth: windowWidth < 768 ? '100%' : '1400px',
      padding: windowWidth < 768 ? '0.75rem' : '2rem 1.5rem',
      width: '100%',
      boxSizing: 'border-box',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <div className="gallery-header">
        <div style={{ width: '100%', overflow: 'hidden' }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '1rem', 
            marginBottom: '0.5rem' 
          }}>
            <Button variant="outline" onClick={() => navigate(`/${gameId}`)} style={{ alignSelf: 'flex-start' }}>
              <ArrowLeft size={18} style={{ marginRight: '0.5rem' }} />
              {t("common.back")}
            </Button>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <Avatar
                userId={userResults.user_id}
                avatarUrl={userResults.avatar_url}
                userName={userResults.user_full_name}
                userEmail={userResults.user_email}
                size={56}
                country={userResults.country}
              />
              <div>
                <h1 style={{ 
                  margin: 0,
                  fontSize: windowWidth < 768 ? '1.5rem' : '2rem',
                  lineHeight: 1.2
                }}>
                  {userResults.user_full_name}
                </h1>
                <p style={{
                  margin: '0.25rem 0 0 0',
                  fontSize: windowWidth < 768 ? '0.9rem' : '1rem',
                  color: '#666',
                  fontWeight: '500'
                }}>
                  {t('results.resultsFor')} «{getTranslatedText(game.name_translations, game.name)}»
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Score Summary */}
      {userResults.answered_count > 0 && (() => {
        // Check if user is a winner
        const userWinner = winners.find(w => w.user_id === userId);
        const totalParticipants = allResults.length;
        
        // Determine medal/position emoji based on winners API
        let positionDisplay = '';
        let userPosition = null;
        
        if (userWinner) {
          userPosition = userWinner.place;
          if (userPosition === 1) {
            positionDisplay = <PlaceMedal place={1} size="lg" />;
          } else if (userPosition === 2) {
            positionDisplay = <PlaceMedal place={2} size="lg" />;
          } else if (userPosition === 3) {
            positionDisplay = <PlaceMedal place={3} size="lg" />;
          }
        } else {
          // Not a winner, calculate position by score
          const sortedResults = [...allResults].sort((a, b) => b.total_score - a.total_score);
          userPosition = sortedResults.findIndex(r => r.user_id === userId) + 1;
          positionDisplay = `${userPosition}`;
        }
        
        return (
          <div className="results-card">
            <div className="results-container">
              <div className="results-info">
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
                  {t("results.result")}
                </h3>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.95rem', opacity: 0.9 }}>
                  {t('results.answersOf').replace('{count}', userResults.answered_count).replace('{total}', userResults.total_panoramas)}
                </p>
                <div className="user-result-position">
                  {userPosition <= 3 && positionDisplay && (
                    <div className="user-result-position__medal">{positionDisplay}</div>
                  )}
                  <div className="user-result-position__text">
                    <span className="user-result-position__place">
                      {userPosition === 1 ? t('game.firstPlace')
                        : userPosition === 2 ? t('game.secondPlace')
                        : userPosition === 3 ? t('game.thirdPlace')
                        : `${userPosition} ${t('game.place')}`}
                    </span>
                    <span className="user-result-position__participants">
                      {t('game.ofParticipants').replace('{count}', totalParticipants)}
                    </span>
                    {userWinner && userWinner.achieved_at && (
                      <span
                        className="user-result-position__achieved-at"
                        data-testid="user-result-achieved-at"
                        style={{
                          display: 'block',
                          marginTop: '0.25rem',
                          fontSize: '0.85rem',
                          opacity: 0.85,
                        }}
                      >
                        {t('userResults.achievedAt')}:{' '}
                        {new Date(userWinner.achieved_at).toLocaleString(undefined, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            
            <div className="results-accuracy">
              <div className="accuracy-number">
                {accuracyPercent}%
              </div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9, marginTop: '0.5rem' }}>
                {t("results.accuracy")}
              </div>
            </div>

            <div className="results-score">
              <div className="score-number">
                <Trophy size={28} style={{ display: 'inline', marginRight: '0.5rem', marginBottom: '0.25rem' }} />
                {userResults.total_score.toLocaleString('ru-RU')}
              </div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9, marginTop: '0.25rem' }}>
                {t('results.outOfPoints').replace('{max}', maxPossibleScore.toLocaleString())}
              </div>
            </div>

            {/* Hotspots Found */}
            {hotspotsStats && hotspotsStats.total_hotspots > 0 && (
              <div className="results-hotspots" style={{
                textAlign: 'center',
                padding: '0.75rem 1rem',
                background: 'rgba(139, 92, 246, 0.1)',
                borderRadius: '12px',
                border: '1px solid rgba(139, 92, 246, 0.2)'
              }}>
                <div style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 700,
                  color: '#8b5cf6'
                }}>
                  <Target size={24} style={{ display: 'inline', marginRight: '0.5rem', marginBottom: '0.25rem' }} />
                  {hotspotsStats.found_hotspots}/{hotspotsStats.total_hotspots}
                </div>
                <div style={{ fontSize: '0.85rem', opacity: 0.9, marginTop: '0.25rem' }}>
                  {t("game.objects")}
                </div>
              </div>
            )}
          </div>
        </div>
        );
      })()}

      {/* Share Buttons - only for the user who achieved this result */}
      {user && user.id === userId && userResults && userResults.answered_count > 0 && (
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: windowWidth < 768 ? '1rem' : '1.5rem',
          marginBottom: '2rem',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e5e5'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1rem'
          }}>
            <Share2 size={20} />
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>
              {t('userResults.shareResult')}
            </h3>
          </div>
          <div style={{
            display: 'flex',
            flexDirection: windowWidth < 768 ? 'column' : 'row',
            gap: '0.75rem',
            flexWrap: 'wrap'
          }}>
            <Button
              onClick={handleShareVK}
              style={{
                background: '#0077FF',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: 'pointer',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                flex: windowWidth < 768 ? '1' : 'none'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.07 2H8.93C3.33 2 2 3.33 2 8.93v6.14C2 20.67 3.33 22 8.93 22h6.14c5.6 0 6.93-1.33 6.93-6.93V8.93C22 3.33 20.67 2 15.07 2zm3.48 13.19c-.21.53-.75.89-1.26.89h-1.46c-.73 0-.96-.58-2.27-1.89-1.15-1.09-1.66-1.24-1.95-1.24-.4 0-.51.11-.51.64v1.72c0 .46-.15.74-1.35.74-1.98 0-4.18-1.2-5.73-3.44-2.32-3.3-2.96-5.79-2.96-6.3 0-.29.11-.56.64-.56h1.46c.48 0 .66.22.85.73.98 2.82 2.62 5.29 3.3 5.29.25 0 .37-.12.37-.76v-2.97c-.08-1.26-.74-1.37-.74-1.82 0-.24.19-.48.5-.48h2.29c.4 0 .55.22.55.7v4c0 .4.18.55.3.55.25 0 .45-.15.91-.61 1.4-1.58 2.41-4.02 2.41-4.02.13-.28.35-.56.89-.56h1.46c.55 0 .67.28.55.7-.17.96-2.17 4.15-2.17 4.15-.21.34-.29.49 0 .88.21.29.9.88 1.36 1.41.84.98 1.48 1.8 1.65 2.37z"/>
              </svg>
              {t('userResults.vkontakte')}
            </Button>

            <Button
              onClick={handleShareTelegram}
              style={{
                background: '#0088cc',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: 'pointer',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                flex: windowWidth < 768 ? '1' : 'none'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.02-1.96 1.25-5.54 3.67-.52.36-.99.53-1.42.52-.47-.01-1.37-.26-2.03-.48-.82-.27-1.47-.42-1.42-.88.03-.24.37-.48 1.02-.73 3.99-1.73 6.66-2.87 8-3.42 3.82-1.59 4.61-1.87 5.13-1.87.11 0 .37.03.54.17.14.11.18.26.2.38.02.08.03.35.01.54z"/>
              </svg>
              Telegram
            </Button>

            <Button
              onClick={handleShareWhatsApp}
              style={{
                background: '#25D366',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: 'pointer',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                flex: windowWidth < 768 ? '1' : 'none'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </Button>

            <Button
              onClick={handleCopyLink}
              variant="outline"
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: 'pointer',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                flex: windowWidth < 768 ? '1' : 'none'
              }}
            >
              {copied ? <Check size={18} /> : <Copy size={18} />}
              {copied ? 'Скопировано!' : 'Копировать ссылку'}
            </Button>
            
            <Button
              onClick={handleGenerateCard}
              disabled={generatingCard}
              style={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: generatingCard ? 'wait' : 'pointer',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                flex: windowWidth < 768 ? '1' : 'none',
                opacity: generatingCard ? 0.7 : 1
              }}
            >
              <Image size={18} />
              {generatingCard ? t('results.creating') : t('results.downloadCard')}
            </Button>

            {/* Challenge button - only for own results */}
            {user && user.id === userId && (
              <Button
                onClick={handleCreateChallenge}
                disabled={creatingChallenge}
                style={{
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: creatingChallenge ? 'wait' : 'pointer',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flex: windowWidth < 768 ? '1' : 'none',
                  opacity: creatingChallenge ? 0.7 : 1
                }}
              >
                <Sword size={18} />
                {creatingChallenge ? 'Создание...' : 'Бросить вызов'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Panoramas Grid */}
      {panoramas.length === 0 ? (
        <div className="empty-state">
          <Upload size={64} strokeWidth={1} />
          <h2>{t('userResults.thereNoPanoramasGame')}</h2>
        </div>
      ) : (
        <div className="gallery-grid">
          {panoramas.map((panorama) => {
            const result = getPanoramaResult(panorama.id);
            const panoramaHotspots = hotspotsStats?.by_panorama?.[panorama.id];
            const isObjectsOnly = game?.game_mode === 'objects_only';
            const hasHotspots = panoramaHotspots && panoramaHotspots.total > 0;
            const isActive = isObjectsOnly ? hasHotspots : !!result;
            
            return (
              <div
                key={panorama.id}
                className="panorama-card"
                onClick={() => {
                  if (isObjectsOnly && hasHotspots) { navigate(`/view/${panorama.id}?viewUserId=${userId}`); } else if (panorama.latitude && panorama.longitude && result) {
                    // Navigate to guess page with viewUserId parameter to show this user's result
                    navigate(`/${gameId}/guess/${panorama.id}?viewUserId=${userId}`);
                  }
                }}
                style={{ 
                  position: 'relative',
                  cursor: isActive ? 'pointer' : 'not-allowed',
                  opacity: isActive ? 1 : 0.6
                }}
              >
                <div className="panorama-preview">
                  {panorama.preview_path ? (
                    <img
                      src={`${API}/panoramas/${panorama.id}/preview`}
                      alt={panorama.name}
                    />
                  ) : (
                    <div className="no-preview">
                      <Upload size={32} strokeWidth={1} />
                    </div>
                  )}
                  
                  {/* Result Overlay - different for objects_only games */}
                  {isObjectsOnly && hasHotspots ? (
                    <div style={{
                      position: 'absolute',
                      top: 0, left: 0, right: 0, bottom: 0,
                      background: panoramaHotspots.found === panoramaHotspots.total ? 'rgba(34, 197, 94, 0.85)' : 'rgba(0, 0, 0, 0.75)',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      color: 'white', padding: '1rem'
                    }}>
                      <Target size={32} style={{ marginBottom: '0.5rem', color: panoramaHotspots.found === panoramaHotspots.total ? '#fff' : '#f59e0b' }} />
                      <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>
                        {panoramaHotspots.found}/{panoramaHotspots.total}
                      </div>
                      <div style={{ fontSize: '0.8rem', opacity: 0.9, marginTop: '0.25rem' }}>
                        {panoramaHotspots.found === panoramaHotspots.total ? 'Все найдено!' : 'объектов найдено'}
                      </div>
                    </div>
                  ) : result ? (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0, 0, 0, 0.75)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      padding: '1rem'
                    }}>
                      <Trophy size={32} style={{ marginBottom: '0.5rem' }} />
                      <div style={{ fontSize: '2rem', fontWeight: 700 }}>
                        {result.score}
                      </div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.9, marginTop: '0.25rem' }}>
                        {result.distance < 1000 
                          ? `${Math.round(result.distance)} ${t('common.meters')}`
                          : `${(result.distance / 1000).toFixed(1)} ${t('common.kilometers')}`}
                      </div>
                      {/* Hotspots for this panorama */}
                      {hotspotsStats && hotspotsStats.by_panorama && hotspotsStats.by_panorama[panorama.id] && (
                        <div style={{ 
                          fontSize: '0.8rem', 
                          marginTop: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          color: hotspotsStats.by_panorama[panorama.id].found > 0 ? '#a78bfa' : 'rgba(255,255,255,0.6)'
                        }}>
                          <Target size={14} />
                          {hotspotsStats.by_panorama[panorama.id].found}/{hotspotsStats.by_panorama[panorama.id].total}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0, 0, 0, 0.5)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      padding: '1rem',
                      textAlign: 'center'
                    }}>
{t('userResults.notAnswered')}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {allResults.length > 0 && (
        <div style={{ marginTop: '3rem' }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            marginBottom: '1.5rem',
            color: '#2d3748'
          }}>
            {t('userResults.resultsTable')}
          </h2>
          
          <ResultsTable
            gameId={gameId}
            results={allResults}
            winners={winners}
            game={game}
            currentUserId={userId}
            showPrizePlaceholders={false}
            itemsPerPage={15}
          />
        </div>
      )}
      <div style={{ height: '3rem' }} />
    </div>
    <SiteFooter />
    </>
  );
};

export default UserResults;
